import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

export type ToastType = 'success' | 'error' | 'info';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Default,
  styles: [`
    :host {
      pointer-events: none;
    }
    .toast-wrapper {
      pointer-events: all;
      position: fixed;
      top: 24px;
      right: 24px;
      z-index: 99999;
      min-width: 320px;
      max-width: 420px;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08);
      animation: slideIn 0.3s cubic-bezier(.4,0,.2,1);
      background: #fff;
    }
    .toast-wrapper.dismissing {
      animation: slideOut 0.35s cubic-bezier(.4,0,.2,1) forwards;
    }
    .toast-header-success {
      background: #E9F5EF;
      color: #178353;
    }
    .toast-header-error {
      background: #FEF2F2;
      color: #B42318;
    }
    .toast-header-info {
      background: #EFF6FF;
      color: #1D4ED8;
    }
    .toast-close-btn-success { color: #178353; border-color: #178353; }
    .toast-close-btn-success:hover { background: #178353; color: #fff; }
    .toast-close-btn-error { color: #B42318; border-color: #B42318; }
    .toast-close-btn-error:hover { background: #B42318; color: #fff; }
    .toast-close-btn-info { color: #1D4ED8; border-color: #1D4ED8; }
    .toast-close-btn-info:hover { background: #1D4ED8; color: #fff; }
    @keyframes slideIn {
      from { opacity: 0; transform: translateX(60px) scale(0.96); }
      to   { opacity: 1; transform: translateX(0) scale(1); }
    }
    @keyframes slideOut {
      from { opacity: 1; transform: translateX(0) scale(1); }
      to   { opacity: 0; transform: translateX(60px) scale(0.96); }
    }
  `],
  template: `
    <div class="toast-wrapper" [class.dismissing]="dismissing">
      <!-- Header -->
      <div
        class="px-5 py-4 flex items-center justify-between gap-3"
        [class.toast-header-success]="type === 'success'"
        [class.toast-header-error]="type === 'error'"
        [class.toast-header-info]="type === 'info'"
      >
        <div class="flex items-center gap-2.5">
          <!-- Icon -->
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
            [style.background]="iconBg"
            [style.color]="iconColor"
          >
            <span *ngIf="type === 'success'">✓</span>
            <span *ngIf="type === 'error'">✕</span>
            <span *ngIf="type === 'info'">ℹ</span>
          </div>
          <div>
            <p class="font-bold text-[15px] leading-tight">
              {{ title }}
            </p>
          </div>
        </div>
        <!-- X button -->
        <button
          (click)="close()"
          class="w-7 h-7 rounded-full border flex items-center justify-center text-xs font-bold transition-all shrink-0"
          [class.toast-close-btn-success]="type === 'success'"
          [class.toast-close-btn-error]="type === 'error'"
          [class.toast-close-btn-info]="type === 'info'"
        >✕</button>
      </div>

      <!-- Body -->
      <div class="px-5 py-4">
        <p class="text-neutral-600 text-sm leading-relaxed">{{ message }}</p>
      </div>
    </div>
  `,
})
export class ToastComponent {
  @Input() message = '';
  @Input() type: ToastType = 'success';
  @Input() translate!: TranslateService;
  dismissing = false;
  close: () => void = () => {};

  get title(): string {
    if (!this.translate) {
      if (this.type === 'success') return 'Operation Successful';
      if (this.type === 'error')   return 'An Error Occurred';
      return 'Notice';
    }
    if (this.type === 'success') return this.translate.instant('toast.success_title');
    if (this.type === 'error')   return this.translate.instant('toast.error_title');
    return this.translate.instant('toast.info_title');
  }

  get iconBg(): string {
    if (this.type === 'success') return '#178353';
    if (this.type === 'error')   return '#B42318';
    return '#1D4ED8';
  }

  get iconColor(): string {
    return '#fff';
  }
}
